import * as THREE from 'three';
import type { WeaponId } from '../weapons/WeaponDefs';

export interface LoadedViewWeapon {
  root: THREE.Group;
  /** Group that holds orientation/scale (for live tuning). */
  oriented: THREE.Group;
  /** Approximate muzzle tip in local space (after normalize). */
  muzzle: THREE.Object3D;
  /** Approximate iron-sight aim point. */
  ironSight: THREE.Object3D;
  /** Right-hand grip socket (procedural arms). */
  gripR?: THREE.Object3D;
  /** Left-hand grip / handguard / pump socket. */
  gripL?: THREE.Object3D;
  /** Casing eject point. */
  ejectPort?: THREE.Object3D;
  /** Shotgun pump group (slides in +Z). */
  pump?: THREE.Object3D;
  /** Pistol slide (kicks in +Z). */
  slide?: THREE.Object3D;
  /** Break-action barrels, hinged at the bottom for open/eject/reload. */
  barrelBreak?: THREE.Object3D;
  /** Left chamber shell (break-action). */
  shellL?: THREE.Object3D;
  /** Right chamber shell (break-action). */
  shellR?: THREE.Object3D;
  /** Detachable pistol magazine. */
  mag?: THREE.Object3D;
  /** Optional pistol suppressor, parented to the frame (not the slide). */
  silencer?: THREE.Object3D;
  /** Loaded RPG warhead on the tube tip. */
  rocket?: THREE.Object3D;
}

export interface ViewmodelTuning {
  /** Extra euler (YXZ: pitch, yaw, roll) after auto-orient. */
  rot: THREE.Euler;
  /** Extra position nudge in pose space. */
  pos: THREE.Vector3;
  /** Uniform scale after normalize-to-unit. */
  scale: number;
  /**
   * If true, auto-align longest bbox axis to camera -Z and
   * try to keep the "top" of the mesh facing +Y.
   */
  autoOrient: boolean;
}

/** Per-weapon hand-tuned offsets (refined via screenshots). */
export const VIEWMODEL_TUNING: Record<WeaponId, ViewmodelTuning> = {
  pistol: {
    rot: new THREE.Euler(0.42, Math.PI, 0, 'YXZ'),
    pos: new THREE.Vector3(-0.032, 0.01, 0.0),
    scale: 0.28,
    autoOrient: false,
  },
  shotgun: {
    rot: new THREE.Euler(0.32, Math.PI + 0.32, 0, 'YXZ'),
    pos: new THREE.Vector3(-0.04, -0.02, 0.08),
    scale: 0.5,
    autoOrient: false,
  },
  mp5: {
    rot: new THREE.Euler(0.078, -1.732, 0.348, 'YXZ'),
    pos: new THREE.Vector3(-0.037, 0.024, 0.02),
    scale: 0.25,
    autoOrient: false,
  },
  ak47: {
    rot: new THREE.Euler(0.22, -Math.PI / 2, 0, 'YXZ'),
    pos: new THREE.Vector3(0.04, -0.06, 0.04),
    scale: 0.26,
    autoOrient: false,
  },
  sawedoff: {
    rot: new THREE.Euler(0, 0, 0, 'YXZ'),
    pos: new THREE.Vector3(0, 0, 0),
    scale: 1,
    autoOrient: false,
  },
  punch: {
    rot: new THREE.Euler(0, 0, 0, 'YXZ'),
    pos: new THREE.Vector3(0, 0, 0),
    scale: 1,
    autoOrient: false,
  },
  gl: {
    rot: new THREE.Euler(0.12, 0, 0, 'YXZ'),
    pos: new THREE.Vector3(0.02, -0.02, 0.04),
    scale: 1,
    autoOrient: false,
  },
  rpg: {
    rot: new THREE.Euler(0.1, 0, 0, 'YXZ'),
    pos: new THREE.Vector3(0.02, -0.03, 0.02),
    scale: 1,
    autoOrient: false,
  },
};
